/**
 * Google Analytics 4 统计插件
 * 
 * 使用方法：
 * 1. 在 Google Analytics 创建 GA4 媒体资源
 * 2. 获取 Measurement ID (格式: G-XXXXXXXXXX)
 * 3. 替换下方的 GA_MEASUREMENT_ID
 */

// ⚠️ 请替换为你的 Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-DZ08S8WV3R'

// 是否启用调试模式（开发环境）
const DEBUG_MODE = process.env.NODE_ENV === 'development'

/**
 * 初始化 Google Analytics
 */
export function initGA() {
  if (GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    console.warn('[Analytics] 请先配置 Google Analytics Measurement ID')
    return
  }

  try {
    // 动态加载 gtag.js
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    
    // 添加跨域属性，让错误信息更详细
    script.crossOrigin = 'anonymous'
    
    // 处理脚本加载错误（网络问题、被拦截等）
    script.onerror = () => {
      if (DEBUG_MODE) {
        console.warn('[Analytics] Google Analytics 脚本加载失败（可能被浏览器/插件拦截）')
      }
    }
    
    document.head.appendChild(script)

    // 初始化 dataLayer
    window.dataLayer = window.dataLayer || []
    window.gtag = function() {
      try {
        window.dataLayer.push(arguments)
      } catch (e) {
        // 静默处理错误
      }
    }

    window.gtag('js', new Date())
    window.gtag('config', GA_MEASUREMENT_ID, {
      debug_mode: DEBUG_MODE,
      send_page_view: false // 手动控制页面浏览事件
    })

    if (DEBUG_MODE) {
      console.log('[Analytics] Google Analytics 已初始化:', GA_MEASUREMENT_ID)
    }
  } catch (e) {
    if (DEBUG_MODE) {
      console.warn('[Analytics] 初始化失败:', e.message)
    }
  }
}

/**
 * 追踪页面浏览 (PV)
 * @param {string} path - 页面路径
 * @param {string} title - 页面标题
 */
export function trackPageView(path, title) {
  try {
    if (!window.gtag) return

    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title,
      page_location: window.location.href
    })

    if (DEBUG_MODE) {
      console.log('[Analytics] 页面浏览:', { path, title })
    }
  } catch (e) {
    // 静默处理
  }
}

/**
 * 追踪自定义事件
 * @param {string} eventName - 事件名称
 * @param {object} params - 事件参数
 */
export function trackEvent(eventName, params = {}) {
  try {
    if (!window.gtag) return

    window.gtag('event', eventName, params)

    if (DEBUG_MODE) {
      console.log('[Analytics] 自定义事件:', eventName, params)
    }
  } catch (e) {
    // 静默处理
  }
}

/**
 * 追踪工具使用
 * @param {string} toolName - 工具名称
 * @param {string} action - 操作类型
 */
export function trackToolUsage(toolName, action = 'use') {
  trackEvent('tool_usage', {
    tool_name: toolName,
    action: action,
    event_category: 'engagement'
  })
}

/**
 * 追踪按钮点击
 * @param {string} buttonName - 按钮名称
 * @param {string} location - 按钮位置
 */
export function trackButtonClick(buttonName, location = '') {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: location,
    event_category: 'interaction'
  })
}

/**
 * 追踪外部链接点击
 * @param {string} url - 链接地址
 */
export function trackOutboundLink(url) {
  trackEvent('click', {
    event_category: 'outbound',
    event_label: url,
    transport_type: 'beacon'
  })
}

/**
 * 追踪搜索
 * @param {string} searchTerm - 搜索词
 */
export function trackSearch(searchTerm) {
  trackEvent('search', {
    search_term: searchTerm
  })
}

/**
 * 追踪错误
 * @param {string} errorMessage - 错误信息
 * @param {string} errorLocation - 错误位置
 */
export function trackError(errorMessage, errorLocation = '') {
  trackEvent('exception', {
    description: errorMessage,
    fatal: false,
    error_location: errorLocation
  })
}

/**
 * 追踪主题切换
 * @param {string} theme - 主题名称 (light/dark)
 */
export function trackThemeChange(theme) {
  trackEvent('theme_change', {
    theme: theme,
    event_category: 'preferences'
  })
}

/**
 * Vue Router 集成
 * @param {object} router - Vue Router 实例
 */
export function setupRouterTracking(router) {
  router.afterEach((to) => {
    // 延迟执行，确保页面标题已更新
    setTimeout(() => {
      trackPageView(to.fullPath, document.title)
    }, 100)
  })
}

export default {
  install(app, options = {}) {
    // 合并配置
    if (options.measurementId) {
      // 允许通过插件选项覆盖 ID
      window.GA_MEASUREMENT_ID = options.measurementId
    }

    // 初始化 GA
    initGA()

    // 挂载到全局属性
    app.config.globalProperties.$analytics = {
      trackPageView,
      trackEvent,
      trackToolUsage,
      trackButtonClick,
      trackOutboundLink,
      trackSearch,
      trackError,
      trackThemeChange
    }

    // 提供 inject 支持
    app.provide('analytics', {
      trackPageView,
      trackEvent,
      trackToolUsage,
      trackButtonClick,
      trackOutboundLink,
      trackSearch,
      trackError,
      trackThemeChange
    })
  }
}
